"use client"

import { memo } from "react"
import { TableRow, TableHead } from "@/components/ui/table"
import { ArrowUp, ArrowDown } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { ColumnType, TableColumnConfig } from "@/types/table"

export type SortDirection = 'asc' | 'desc' | null

export interface SortConfig {
  column: string
  direction: SortDirection
}

export type CustomSortHandler = (a: any, b: any) => number

export interface SortingConfig {
  [key: string]: {
    type: ColumnType
    customSort?: CustomSortHandler
  }
}

// Helper function to get comparable value
const getValue = (value: any): any => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value.toLowerCase()
  return value
}

// Helper function to compare values based on type
const compareValues = (a: any, b: any, type: string): number => {
  // Handle null values - always put them at the end
  if (a === null && b === null) return 0
  if (a === null) return -1
  if (b === null) return 1

  switch (type) {
    case 'number':
    case 'percentage':
    case 'currency':
      return Number(a) - Number(b)
    case 'date':
      return new Date(a).getTime() - new Date(b).getTime()
    default:
      return String(a).localeCompare(String(b))
  }
}

// Helper function to apply sorting to data
const applySorting = <T extends Record<string, any>>(
  data: T[],
  sortConfig: SortConfig,
  sortingConfig: SortingConfig
): T[] => {
  if (!sortConfig.column || !sortConfig.direction) {
    return data
  }

  return [...data].sort((a, b) => {
    const config = sortingConfig[sortConfig.column]
    if (!config) return 0

    if (config.customSort) {
      return sortConfig.direction === 'asc' 
        ? config.customSort(a, b)
        : config.customSort(b, a)
    }

    const aVal = getValue(a[sortConfig.column])
    const bVal = getValue(b[sortConfig.column])
    const comparison = compareValues(aVal, bVal, config.type)
    
    return sortConfig.direction === 'asc' ? comparison : -comparison
  })
}

export interface TableHeaderRowProps<T> {
  columns: TableColumnConfig<T>[]
  onColumnClick: (column: string) => void
  currentSortColumn?: string
  currentSortDirection?: SortDirection
  showCheckbox?: boolean
  isAllChecked?: boolean
  onCheckAll?: (checked: boolean) => void
}

function TableHeaderRow<T>({ 
  columns, 
  onColumnClick,
  currentSortColumn,
  currentSortDirection,
  showCheckbox = false,
  isAllChecked = false,
  onCheckAll
}: TableHeaderRowProps<T>) {
  return (
    <TableRow className="bg-white !border-0 sticky top-0 z-20">
      {showCheckbox && (
        <TableHead className={`p-0 bg-white border-b-[3px] border-b-[#999999] border-r-[1px] border-r-[#dddddd] relative`}>
          <div className="px-4 py-2">
            <Checkbox 
              checked={isAllChecked}
              onCheckedChange={onCheckAll}
            />
          </div>
        </TableHead>
      )}
      {columns.map((column) => (
        <TableHead 
          key={String(column.key)} 
          className={`p-0 bg-white border-b-[3px] border-b-[#999999] border-r-[1px] border-r-[#dddddd] relative`}
        >
          <button 
            className="px-4 py-2 w-full text-left flex items-center gap-2 hover:text-foreground text-sm text-black" 
            onClick={() => column.sortable && onColumnClick(String(column.key))}
          >
            {column.label}
            {column.sortable && currentSortColumn === column.key && (
              currentSortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : 
              currentSortDirection === 'desc' ? <ArrowDown className="h-4 w-4" /> : 
              null
            )}
          </button>
        </TableHead>
      ))}
    </TableRow>
  )
}

const MemoizedTableHeaderRow = memo(TableHeaderRow) as <T>(props: TableHeaderRowProps<T>) => JSX.Element
export default MemoizedTableHeaderRow

export { applySorting } 